import axios from 'axios';
// Optimized imports - only import actually used types
import { User, Patient, Employee, Supervision } from '../types';
import { errorHandler } from './errorHandler';

// Dynamic API URL detection for production
const getAPIBaseURL = () => {
  // For development, use localhost
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:4000/api';
  }
  
  // For production, ALWAYS use the current domain's API endpoint
  // This prevents cross-environment calls when staging builds are promoted to production
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/api`;
  }
  
  // Fallback for server-side rendering (use relative path)
  return '/api';
};

const API_BASE_URL = getAPIBaseURL();

// Create/get a stable session id for CSRF binding across requests
const getOrCreateSessionId = (): string => {
  try {
    const existing = localStorage.getItem('sessionId');
    if (existing) return existing;
  } catch {}
  const generated = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `sid_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  try { localStorage.setItem('sessionId', generated); } catch {}
  return generated;
};
const SESSION_ID = getOrCreateSessionId();

// Create axios instance with default configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Include cookies in requests
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Session-Id': SESSION_ID,
  }
});

// CSRF token management
let csrfToken: string | null = null;

// Get CSRF token from server
export const getCSRFToken = async (): Promise<string> => {
  if (csrfToken) {
    return csrfToken;
  }
  
  try {
    const response = await axios.get('/api/csrf-token', { 
      withCredentials: true,
      baseURL: API_BASE_URL.replace('/api', ''), // Use base URL without /api since we include it in path
      headers: { 'X-Session-Id': SESSION_ID }
    });
    csrfToken = response.data.csrfToken;
    if (!csrfToken) {
      throw new Error('No CSRF token received from server');
    }
    return csrfToken;
  } catch (error) {
    console.error('Failed to get CSRF token:', error);
    throw new Error('Failed to initialize security token');
  }
};

// Request interceptor to add CSRF token to all requests
api.interceptors.request.use(
  async (config) => {
    // Skip CSRF for GET requests
    if (config.method !== 'get') {
      try {
        const token = await getCSRFToken();
        config.headers['X-CSRF-Token'] = token;
      } catch (error) {
        console.error('Failed to add CSRF token to request:', error);
        // Continue with request even if CSRF token fails
      }
    }
    
    // Add additional security headers
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    // Ensure session id is always present for CSRF secret lookup
    if (!config.headers['X-Session-Id']) {
      config.headers['X-Session-Id'] = SESSION_ID;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle CSRF token refresh and security errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Handle CSRF token errors
    if (error.response?.status === 403 && error.response?.data?.error?.includes('CSRF')) {
      // Clear cached CSRF token and retry once
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        csrfToken = null; // Clear cached token
        
        try {
          const token = await getCSRFToken();
          originalRequest.headers['X-CSRF-Token'] = token;
          return api(originalRequest);
        } catch (csrfError) {
          console.error('Failed to refresh CSRF token:', csrfError);
          // Process error through error handler
          const frontendError = errorHandler.handleAPIError(error, {
            component: 'api',
            action: 'csrf_token_refresh'
          });
          return Promise.reject(frontendError);
        }
      }
    }
    
    // Handle authentication errors
    if (error.response?.status === 401) {
      // Clear any cached tokens
      csrfToken = null;
      
      // Don't show error toasts for auth failures on login page or auth check endpoints
      const isOnLoginPage = window.location.pathname.includes('/login');
      const isOnPasswordResetPage = window.location.pathname.includes('/reset-password') || window.location.pathname.includes('/set-password');
      const isAuthCheckEndpoint = originalRequest.url?.includes('/auth/me');
      const isPasswordChangeEndpoint = originalRequest.url?.includes('/auth/change-password') || originalRequest.url?.includes('/auth/forced-password-change');
      const isMFAEndpoint = originalRequest.url?.includes('/auth/mfa/');
      const shouldSuppressError = isOnLoginPage || isOnPasswordResetPage || isAuthCheckEndpoint;
      
      // For password change flows and MFA flows, bubble the raw Axios error so the caller can show the specific message
      if (isPasswordChangeEndpoint || isMFAEndpoint) {
        return Promise.reject(error);
      }
      
      // Build context with suppressToast flag BEFORE processing error
      const errorContext = {
        component: 'api',
        action: 'authentication',
        ...(shouldSuppressError && { suppressToast: true })
      };


      // Process through error handler with suppressToast already set
      let frontendError = errorHandler.handleAPIError(error, errorContext);
      
      // Redirect to login if not already on login page, password reset page, password change validation failure (401), or MFA validation failure (401)
      if (!isOnLoginPage && !isOnPasswordResetPage && !isPasswordChangeEndpoint && !isMFAEndpoint) {
        // Add sessionExpired parameter to indicate this is due to session timeout
        window.location.href = '/login?sessionExpired=true';
      }
      
      return Promise.reject(frontendError);
    }
    
    // Handle rate limiting
    if (error.response?.status === 429) {
      // Create a user-friendly rate limiting error without showing excessive toasts
      const frontendError = {
        code: 'RATE_LIMIT_ERROR',
        message: 'Too many requests',
        severity: 'medium' as const,
        userMessage: 'Please wait a moment before trying again',
        retryable: true,
        context: { 
          suppressToast: true, // Suppress toast to avoid spam
          retryAfter: error.response?.headers['retry-after'] || 30 
        },
        timestamp: new Date()
      };
      
      // Only show one rate limit message per 10 seconds to avoid spam
      const lastRateLimitToast = sessionStorage.getItem('lastRateLimitToast');
      const now = Date.now();
      
      if (!lastRateLimitToast || (now - parseInt(lastRateLimitToast)) > 10000) {
        errorHandler.handleAPIError(error, {
          component: 'api',
          action: 'rate_limiting'
        });
        sessionStorage.setItem('lastRateLimitToast', now.toString());
      }
      
      return Promise.reject(frontendError);
    }
    
    // Handle 409 Conflict errors specially - pass through unchanged for component-level handling
    if (error.response?.status === 409) {
      // Don't process 409 errors through the generic error handler
      // Let them reach component-level error handling with original response data intact
      return Promise.reject(error);
    }
    
    // Process all other errors through the error handler
    const frontendError = errorHandler.handleAPIError(error, {
      component: 'api',
      action: originalRequest.url || 'unknown'
    });
    
    return Promise.reject(frontendError);
  }
);

// Enhanced error handling helper
const handleApiError = (error: any): string => {
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  
  if (error.response?.status === 403) {
    return 'Access forbidden. Please check your permissions.';
  }
  
  if (error.response?.status === 429) {
    return 'Too many requests. Please try again later.';
  }
  
  if (error.response?.status >= 500) {
    return 'Server error. Please try again later.';
  }
  
  if (error.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
};

// Auth API functions with enhanced security
export const login = async (email: string, password: string, rememberMe?: boolean) => {
  try {
    const response = await api.post('/auth/login', { email, password, rememberMe });
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

// MFA verification function
export const verifyMFA = async (totpCode: string, tempToken: string, deviceId?: string) => {
  try {
    const response = await api.post('/auth/verify-mfa', { totpCode, tempToken, deviceId });
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

export const register = async (email: string, password: string, role: string = 'ADMIN') => {
  try {
    const response = await api.post('/auth/register', { email, password, role });
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

export const logout = async () => {
  try {
    const response = await api.post('/auth/logout');
    // Clear cached CSRF token on logout
    csrfToken = null;
    return response.data;
  } catch (error) {
    // Clear token even if logout fails
    csrfToken = null;
    throw new Error(handleApiError(error));
  }
};

// Enhanced password change function with security validation
export const changePassword = async (currentPassword: string, newPassword: string, confirmPassword: string) => {
  try {
    const response = await api.post('/auth/change-password', {
      currentPassword,
      newPassword,
      confirmPassword
    });
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

// Forced password change function
export const forcedPasswordChange = async (newPassword: string, confirmPassword: string) => {
  try {
    const response = await api.post('/auth/forced-password-change', {
      newPassword,
      confirmPassword
    });
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

// Password policy functions
export const getPasswordPolicy = async () => {
  try {
    const response = await api.get('/password-policy/policy');
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

export const validatePassword = async (password: string) => {
  try {
    const response = await api.post('/password-policy/validate', { password });
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

export const getPasswordRequirements = async (userId?: string) => {
  try {
    const url = userId ? `/password-policy/requirements/${userId}` : '/password-policy/requirements';
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

// Password notification functions
export const getNotificationStatus = async () => {
  try {
    const response = await api.get('/password-policy/notification-status');
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

export const getExpiringPasswords = async (days?: number) => {
  try {
    const params = days ? { days } : {};
    const response = await api.get('/password-policy/expiring-passwords', { params });
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

export const sendPasswordNotifications = async (userIds: string[], notificationType: 'warning' | 'urgent' = 'warning') => {
  try {
    const response = await api.post('/password-policy/send-notifications', {
      userIds,
      notificationType
    });
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

// Patient services
export const patientService = {
  async getPatients(
    page: number = 1, 
    limit: number = 25,
    sortBy: string = 'nextSupervisionDue',
    sortOrder: 'asc' | 'desc' = 'asc',
    status?: 'ACTIVE' | 'DISCHARGED',
    search?: string
  ): Promise<{ data: Patient[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
    const params: any = {
      page,
      limit,
      sort: sortBy,
      order: sortOrder
    };

    // Add status filter if provided
    if (status) {
      params.status = status;
    }

    // Name search: every word must match the first or last name
    if (search) {
      params.search = search;
    }

    const response = await api.get('/patients', {
      params
    });
    // Backend returns paginated response in format: { data: [...], pagination: {...} }
    return response.data;
  },

  /**
   * Fetch the COMPLETE patient roster, following pagination to the end.
   *
   * The API silently caps `limit` at 100 rows per request
   * (server/src/lib/apiOptimization.ts). Asking for `limit=1000` in a single
   * request returns 100 rows with no indication that anything was dropped,
   * which is how active patients went missing from the supervision form's
   * dropdown. Always use this when you need every patient, never a single
   * large-limit call.
   *
   * Pass `status` so the server does the filtering: filtering client-side
   * spends the 100-row budget on discharged patients before the active ones
   * are ever returned.
   *
   * The sort default matches getPatients() — soonest supervision due first —
   * because that is the order the patient dropdowns and dashboards are meant
   * to present. Do not change it to alphabetical: due-date order is intended.
   */
  async getAllPatients(
    status?: 'ACTIVE' | 'DISCHARGED',
    sortBy: string = 'nextSupervisionDue',
    sortOrder: 'asc' | 'desc' = 'asc'
  ): Promise<Patient[]> {
    const PAGE_SIZE = 100; // Matches the server's per-request maximum.
    const MAX_PAGES = 200; // Guard against a malformed pagination response.

    const byId = new Map<string, Patient>();
    let page = 1;
    let totalPages = 1;

    while (page <= totalPages && page <= MAX_PAGES) {
      const response = await patientService.getPatients(
        page,
        PAGE_SIZE,
        sortBy,
        sortOrder,
        status
      );

      const rows = response?.data || [];
      // De-duplicate by id: a record added or discharged mid-fetch shifts the
      // page offsets, which can otherwise repeat a row across pages.
      rows.forEach(patient => byId.set(patient.id, patient));

      totalPages = response?.pagination?.totalPages || 1;
      if (rows.length === 0) break; // Nothing left to read.
      page += 1;
    }

    return Array.from(byId.values());
  },

  async getPatient(id: string): Promise<Patient> {
    const response = await api.get(`/patients/${id}`);
    return response.data;
  },

  async createPatient(patient: Partial<Patient>): Promise<Patient> {
    const response = await api.post('/patients', patient);
    return response.data;
  },

  async updatePatient(id: string, patient: Partial<Patient>): Promise<Patient> {
    const response = await api.put(`/patients/${id}`, patient);
    return response.data;
  },

  async deletePatient(id: string): Promise<void> {
    await api.delete(`/patients/${id}`);
  },
};

// Employee services
export const employeeService = {
  async getEmployees(
    page: number = 1, 
    limit: number = 25,
    sortBy: string = 'nextSupervisionDue',
    sortOrder: 'asc' | 'desc' = 'asc',
    status?: 'ACTIVE' | 'TERMINATED'
  ): Promise<{ data: Employee[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
    const params: any = {
      page,
      limit,
      sort: sortBy,
      order: sortOrder
    };
    
    // Add status filter if provided
    if (status) {
      params.status = status;
    }
    
    const response = await api.get('/employees', { params });
    // Backend returns paginated response in format: { data: [...], pagination: {...} }
    return response.data;
  },

  /**
   * Fetch the COMPLETE employee roster, following pagination to the end.
   * Same 100-row server cap as getAllPatients — see that method for detail.
   */
  async getAllEmployees(
    status?: 'ACTIVE' | 'TERMINATED',
    sortBy: string = 'nextSupervisionDue',
    sortOrder: 'asc' | 'desc' = 'asc'
  ): Promise<Employee[]> {
    const PAGE_SIZE = 100; // Matches the server's per-request maximum.
    const MAX_PAGES = 200; // Guard against a malformed pagination response.

    const byId = new Map<string, Employee>();
    let page = 1;
    let totalPages = 1;

    while (page <= totalPages && page <= MAX_PAGES) {
      const response = await employeeService.getEmployees(
        page,
        PAGE_SIZE,
        sortBy,
        sortOrder,
        status
      );

      const rows = response?.data || [];
      rows.forEach(employee => byId.set(employee.id, employee));

      totalPages = response?.pagination?.totalPages || 1;
      if (rows.length === 0) break;
      page += 1;
    }

    return Array.from(byId.values());
  },

  async getEmployee(id: string): Promise<Employee> {
    const response = await api.get(`/employees/${id}`);
    return response.data;
  },

  async createEmployee(employee: Partial<Employee>): Promise<Employee> {
    const response = await api.post('/employees', employee);
    return response.data;
  },

  async updateEmployee(id: string, employee: Partial<Employee>): Promise<Employee> {
    const response = await api.put(`/employees/${id}`, employee);
    return response.data;
  },

  async deleteEmployee(id: string): Promise<void> {
    await api.delete(`/employees/${id}`);
  },
};

// Enhanced Supervision services with new functionalities from Task 44 integration
export const supervisionService = {
  // Core CRUD operations
  async getSupervisions(): Promise<Supervision[]> {
    const response = await api.get('/supervisions');
    // Handle new paginated response format
    return response.data.data || response.data;
  },

  async getSupervision(id: string): Promise<Supervision> {
    const response = await api.get(`/supervisions/${id}`);
    return response.data;
  },

  async createSupervision(supervision: Partial<Supervision>): Promise<Supervision> {
    const response = await api.post('/supervisions', supervision);
    return response.data;
  },

  async updateSupervision(id: string, supervision: Partial<Supervision>): Promise<Supervision> {
    const response = await api.put(`/supervisions/${id}`, supervision);
    return response.data;
  },

  async deleteSupervision(id: string): Promise<void> {
    await api.delete(`/supervisions/${id}`);
  },

  async getEmployeeSupervisions(employeeId: string): Promise<Supervision[]> {
    const response = await api.get(`/supervisions/employee/${employeeId}`);
    // Handle new paginated response format
    return response.data.data || response.data;
  },

  async getPatientSupervisions(patientId: string): Promise<Supervision[]> {
    const response = await api.get(`/supervisions/patient/${patientId}`);
    // Handle new paginated response format
    return response.data.data || response.data;
  },

  // Enhanced data export functionality (Task 44.7 integration)
  async exportSupervisions(params: {
    startDate?: string;
    endDate?: string;
    supervisionType?: string;
    employeeId?: string;
    patientId?: string;
  } = {}): Promise<void> {
    const searchParams = new URLSearchParams();
    
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);
    if (params.supervisionType && params.supervisionType !== 'ALL') {
      searchParams.append('supervisionType', params.supervisionType);
    }
    if (params.employeeId) searchParams.append('employeeId', params.employeeId);
    if (params.patientId) searchParams.append('patientId', params.patientId);
    
    const queryString = searchParams.toString();
    const url = `/supervisions/export${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url, {
      responseType: 'blob'
    });
    
    // Create download link
    const blob = new Blob([response.data], { type: 'text/csv' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `supervisions-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },

  // Batch PDF printing functionality (Task #190)
  async batchPrintSupervisions(params: {
    supervisionIds: string[];
    outputFormat: 'individual' | 'combined' | 'zip';
  }): Promise<any> {
    const response = await api.post('/supervisions/batch-print', params, {
      timeout: 300000, // 5 minute timeout for large batches
    });
    
    return response.data;
  },

  // Dashboard and analytics functionality (Task 44.5 integration)
  async getDashboardMetrics(): Promise<any> {
    try {
      const [supervisions, employees, patients] = await Promise.all([
        this.getSupervisions(),
        api.get('/employees').then(r => r.data),
        api.get('/patients').then(r => r.data)
      ]);

      // Calculate dashboard metrics
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const todaysSupervisions = supervisions.filter(s => {
        const supervisionDate = new Date(s.supervisionDate);
        return supervisionDate.toDateString() === today.toDateString();
      });

      const completedToday = todaysSupervisions.filter(s => {
        // Determine required signatures based on whether employee was assigned
        const hasRequiredSignatures = s.employeeId 
          ? (s.employeeSignature && s.evaluatorSignature) // Aide supervision: require both
          : s.evaluatorSignature; // Patient-only supervision: require only evaluator
        return s.completedAt || hasRequiredSignatures;
      });

      const overdue = supervisions.filter(s => {
        const supervisionDate = new Date(s.supervisionDate);
        // Determine required signatures based on whether employee was assigned
        const hasRequiredSignatures = s.employeeId 
          ? (s.employeeSignature && s.evaluatorSignature) // Aide supervision: require both
          : s.evaluatorSignature; // Patient-only supervision: require only evaluator
        const isCompleted = s.completedAt || hasRequiredSignatures;
        return supervisionDate < today && !isCompleted;
      });

      return {
        todaysSupervisions: todaysSupervisions.length,
        completedToday: completedToday.length,
        overdue: overdue.length,
        totalActive: supervisions.length,
        employees: employees.length,
        patients: patients.length
      };
    } catch (error) {
      console.error('Error calculating dashboard metrics:', error);
      throw error;
    }
  },

  // Business rules and frequency calculation (Task 44.3 integration)
  calculateNextSupervisionDue(
    lastSupervisionDate: string | Date, 
    insuranceType: string, 
    frequencyRules?: any
  ): Date {
    const lastDate = new Date(lastSupervisionDate);
    
    // Default frequency rules if not provided
    const defaultRules: Record<string, number> = {
      FIDELIS: 90,
      OFA: 90,
      PRIVATE: 90,
      DSS_MEDICAID: 90,
      NACENTIA: 90
    };

    const frequency = frequencyRules?.[insuranceType] || defaultRules[insuranceType] || 90;
    
    const nextDate = new Date(lastDate);
    nextDate.setDate(nextDate.getDate() + frequency);
    
    return nextDate;
  },

  // Background job integration (Task 44.3 integration)
  async updateAllSupervisionDates(): Promise<{ updated: number; errors: string[] }> {
    try {
      const response = await api.post('/supervisions/update-all-dates');
      return response.data;
    } catch (error) {
      console.error('Error updating supervision dates:', error);
      throw error;
    }
  },

  // Bulk operations integration (Task 44.2 integration)
  async bulkUpdateSupervisions(updates: Array<{
    id: string;
    [key: string]: any;
  }>): Promise<any> {
    try {
      const response = await api.post('/supervisions/bulk-update', { updates });
      return response.data;
    } catch (error) {
      console.error('Error performing bulk update:', error);
      throw error;
    }
  },

  // Analytics and reporting integration (Task 44.7 integration)
  async getSupervisionAnalytics(params?: {
    startDate?: string;
    endDate?: string;
    groupBy?: 'month' | 'quarter' | 'year';
    includeCompliance?: boolean;
  }): Promise<any> {
    const searchParams = new URLSearchParams();
    
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    if (params?.groupBy) searchParams.append('groupBy', params.groupBy);
    if (params?.includeCompliance) searchParams.append('includeCompliance', 'true');
    
    const queryString = searchParams.toString();
    const url = `/supervisions/analytics${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url);
    return response.data;
  },

  // Real-time updates integration (Task 44.5 integration)
  async getSupervisionStatusUpdates(since?: Date): Promise<any[]> {
    const params = new URLSearchParams();
    if (since) {
      params.append('since', since.toISOString());
    }
    
    const response = await api.get(`/supervisions/status-updates?${params.toString()}`);
    return response.data;
  },

  // Performance optimization integration
  async getSupervisionsPaginated(params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    filters?: Record<string, any>;
  }): Promise<{
    supervisions: Supervision[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    const searchParams = new URLSearchParams();
    
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    if (params?.filters) {
      Object.entries(params.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          searchParams.append(`filter_${key}`, value.toString());
        }
      });
    }
    
    const queryString = searchParams.toString();
    const url = `/supervisions/paginated${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url);
    return response.data;
  }
};

export const userService = {
  async getUsers(): Promise<{ users: User[] }> {
    const response = await api.get('/users');
    return response.data;
  },

  async getUserById(id: string): Promise<{ user: User }> {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  async createUser(userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role?: string;
    isSuperAdmin?: boolean;
    linkedEmployeeId?: string;
  }): Promise<{ user: User; message: string }> {
    const response = await api.post('/users', userData);
    return response.data;
  },

  async updateUser(id: string, userData: {
    email?: string;
    firstName?: string;
    lastName?: string;
    role?: string;
    isSuperAdmin?: boolean;
    linkedEmployeeId?: string;
  }): Promise<{ user: User; message: string }> {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },

  async toggleUserActive(id: string): Promise<{ user: User; message: string }> {
    const response = await api.patch(`/users/${id}/toggle-active`);
    return response.data;
  },

  async deleteUser(id: string): Promise<{ message: string }> {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },

  async getUserActivity(id: string): Promise<{ activities: any[] }> {
    const response = await api.get(`/users/${id}/activity`);
    return response.data;
  },

  async resetUserPassword(id: string, forcePasswordChange: boolean = true): Promise<{
    message: string;
    temporaryPassword: string;
    user: {
      id: string;
      email: string;
      passwordResetRequired: boolean;
      passwordLastChanged: string;
      passwordExpiresAt: string;
    };
    securityInstructions: string;
  }> {
    const response = await api.post(`/users/${id}/reset-password`, {
      forcePasswordChange
    });
    const data = response.data || {};
    // Normalize server response: map `instructions` (array|string) -> `securityInstructions` (string)
    const instructions = Array.isArray(data.instructions)
      ? data.instructions.join(' ')
      : (data.instructions || '');
    return {
      message: data.message,
      temporaryPassword: data.temporaryPassword,
      user: data.user,
      securityInstructions: instructions
    };
  },
};

// Security monitoring functions
export const getPasswordAuditLogs = async (filters: any = {}) => {
  try {
    const response = await api.get('/password-audit/logs', { params: filters });
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

export const exportPasswordAuditLogs = async (format: 'csv' | 'json' = 'csv', filters: any = {}) => {
  try {
    const response = await api.get('/password-audit/export', {
      params: { ...filters, format },
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `password-audit-${new Date().toISOString().split('T')[0]}.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return { success: true, message: 'Audit logs exported successfully' };
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

// Check authentication status
export const checkAuth = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

// MFA Management functions
export const getMFAStatus = async () => {
  try {
    const response = await api.get('/auth/mfa/status');
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

export const enableMFA = async (data: { password: string }) => {
  try {
    const response = await api.post('/auth/mfa/enable', data);
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

export const sendMFADisableCode = async (data: { password: string }) => {
  try {
    const response = await api.post('/auth/mfa/send-disable-code', data);
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

export const disableMFA = async (data: { password: string; totpCode: string }) => {
  try {
    const response = await api.post('/auth/mfa/disable', data);
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

export const regenerateBackupCodes = async (data: { password: string; totpCode: string }) => {
  try {
    const response = await api.post('/auth/mfa/regenerate-backup-codes', data);
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

// Consolidated authService object for backward compatibility
export const authService = {
  login,
  verifyMFA,
  logout,
  register,
  changePassword,
  forcedPasswordChange,
  getPasswordPolicy,
  validatePassword,
  getPasswordRequirements,
  getNotificationStatus,
  getExpiringPasswords,
  sendPasswordNotifications,
  checkAuth,
  // MFA Management
  getMFAStatus,
  enableMFA,
  sendMFADisableCode,
  disableMFA,
  regenerateBackupCodes
};

// Initialize CSRF token when the module loads
getCSRFToken().catch((error) => {
  console.warn('Failed to initialize CSRF token on module load:', error);
});

export default api; 